import { MAINTENANCE, SLUDGE_EMPTYING } from '../../utils/rikutec';
import { SourceBoardV2 } from '../../utils/types';
import {
    FieldSource,
    FORM_FIELDS,
    formsOf,
    holdsForm,
    SITE_FIELDS,
    SOIL_FORMS,
    SOIL_SELECTOR,
} from './installationFormFields';

// The forms only the pose step holds: the land owner and forwarder steps stop at _form2, _form3 and _form16.
const POSE_FORMS = ['_form4', '_form5', '_form7', '_form8', '_form11', '_form15'];
const OWNER_SIGNATURE_ID = '_form16';
const INSTALLER_SIGNATURE_ID = '_form17';

const isTrue = (value: any) => String(value) === 'true';

const firstInNotes = (notes: any[], sources: FieldSource[], read: (value: any) => string | undefined) => {
    for (const note of notes) {
        for (const { form, key } of sources) {
            const value = read(formsOf(note)[form]?.[key]);
            if (value) {
                return value;
            }
        }
    }
    return undefined;
};

const readText = (value: any) =>
    typeof value === 'string' || typeof value === 'number' ? String(value).trim().toUpperCase() : undefined;

const readAnswer = (value: any) => {
    if (isTrue(value?.yes)) {
        return 'YES';
    }
    return isTrue(value?.no) ? 'NO' : undefined;
};

const soilTypologyOf = (notes: any[]) => {
    const note = notes.find((candidate) => {
        const forms = formsOf(candidate);
        return SOIL_SELECTOR in forms || SOIL_FORMS.some((form) => form in forms);
    });
    if (!note) {
        return undefined;
    }
    const forms = formsOf(note);
    const selected = SOIL_FORMS.find((form) => isTrue(forms[SOIL_SELECTOR]?.radio?.[form]));
    const present = SOIL_FORMS.filter((form) => form in forms);
    const typology = selected || (present.length === 1 ? present[0] : undefined);
    return typology?.toUpperCase();
};

const sourcesOf = (fields: { field: string; sources: FieldSource[] }[], field: string) =>
    fields.find((candidate) => candidate.field === field)?.sources ?? [];

/** One whole address, like the view: mixing entered and client fields could describe a place that does not exist. */
const installationSiteOf = (notes: any[]) => {
    const entered = (field: string) => firstInNotes(notes, sourcesOf(SITE_FIELDS, field), readText);
    const site = {
        address: entered('installationAddress'),
        postalCode: entered('installationPostalCode'),
        city: entered('installationCity'),
        country: entered('installationCountry'),
    };
    // A country alone does not count as an entered site.
    if (site.address || site.postalCode || site.city) {
        return site;
    }
    const client = (field: string) => firstInNotes(notes, sourcesOf(FORM_FIELDS, field), readText);
    return {
        address: client('clientAddress'),
        postalCode: client('clientPostalCode'),
        city: client('clientCity'),
        country: client('clientCountry'),
    };
};

const hasSignature = (notes: any[], id: string) =>
    notes.some((note) =>
        (note?.data?.signatures || []).some(
            (signature: any) => signature?.id === id && !!signature?.customFields?.imageDataBase64,
        ),
    );

const tagOf = (note: any) => note?.data?.customFields?.tags?.[0];

/**
 * What the forms panel shows, read from the history of an installation (its notes, most recent first).
 *
 * Rikutec runs several processes side by side, with different nodes and field names (see installationFormFields and
 * docs/rikutec-form-fields.md): the same form sits on the pose step, on the land owner step or, copied, on both
 * commissioning steps. A note does not say which node wrote it, but a form keeps its name wherever it is stored, so each
 * field is looked up by form and key across all the notes.
 *
 * A form held by the history comes as a whole, its empty fields as null, so that it replaces another copy of that form
 * rather than mixing with it. A form it does not hold is left out.
 */
const installationFromHistory = (notes: any[]): Partial<SourceBoardV2> => {
    const fields: any = {};

    FORM_FIELDS.forEach(({ field, type, sources }) => {
        if (sources.some(({ form }) => holdsForm(notes, form))) {
            fields[field] = firstInNotes(notes, sources, type === 'answer' ? readAnswer : readText) ?? null;
        }
    });

    const clientNames = [fields.clientName, fields.clientLastName].filter(Boolean);
    if (holdsForm(notes, '_form3')) {
        fields.clientFullName = clientNames.length ? clientNames.join(' ') : null;
    }
    fields.soilTypology = soilTypologyOf(notes);

    if (holdsForm(notes, '_form3') || holdsForm(notes, '_form4')) {
        const site = installationSiteOf(notes);
        fields.installationAddress = site.address ?? null;
        fields.installationPostalCode = site.postalCode ?? null;
        fields.installationCity = site.city ?? null;
        fields.installationCountry = site.country ?? null;
    }

    fields.hasOwnerSignature = hasSignature(notes, OWNER_SIGNATURE_ID) || undefined;
    fields.hasInstallerSignature = hasSignature(notes, INSTALLER_SIGNATURE_ID) || undefined;

    const hasReserves = holdsForm(notes, '_form1');
    const works = notes.find((note) => /^travaux de pose effectu/i.test(note?.name || ''));
    const pose = notes.find((note) => POSE_FORMS.some((form) => holdsForm([note], form)));
    const reserves = hasReserves ? notes.find((note) => holdsForm([note], '_form1')) : undefined;
    fields.readyToDeliverDate = notes.find((note) => /^prête à livrer$/i.test(note?.name || ''))?.createdAt;
    fields.productInstallationDate = works?.createdAt;
    fields.poseDate = (pose || works)?.createdAt;
    fields.reserveLiftDate = reserves?.createdAt;
    fields.productLastMaintenanceDate = notes.find((note) => tagOf(note) === MAINTENANCE)?.createdAt;
    fields.productLastEmptyingDate = notes.find((note) => tagOf(note) === SLUDGE_EMPTYING)?.createdAt;

    return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined));
};

export default installationFromHistory;
